import React, { useCallback, useEffect, useState } from "react";
import { VirtuosoGrid } from "react-virtuoso";
import {
  addParamsURL,
  bytesToHumanReadable,
  downloadFile,
  getArrtoDate,
  getFileNameFromDisposition,
  replaceMultipleSpaces,
} from "../../util/helper";
import decryptUrl from "../../util/img-decryption";
import Loader from "../UI/Loader/Loader";
import { AvatarV2, Button, Icon, Notifications } from "@jds/core";
import { Box, Checkbox, Skeleton } from "@mui/material";

import brokenImg from "../../assets/png/brokenImg.png";

import classes from "./CardGallery.module.css";
import useApiHttp from "../../hooks/use-http";
import { useParams } from "react-router-dom";
import {
  openFileSideBar,
  setFileDetails,
} from "../../store/state-slices/fileDetailsSidebar";
import { useDispatch } from "react-redux";

const gridComponents = {
  Item: ({ children, ...props }) => (
    <div {...props} className={`${classes["grid-item"]}`}>
      {children}
    </div>
  ),
  List: React.forwardRef(({ style, children }, ref) => (
    <div ref={ref} className={`${classes["grid-list"]}`} style={style}>
      {children}
    </div>
  )),
};
/* ===============================
   IMAGE ITEM COMPONENT
   =============================== */
function ImageItem({
  img,
  onCheckBoxClick,
  isActive,
  index,
  isPublic,
  isAdmin,
  selectedTab,
  onClick = () => {},
  selectedId = [],
  isFocused = false,
  onTileFocus = () => {},
  registerPhotoTileRef = () => {},
}) {
  const eventFID = localStorage.getItem("eventFID");
  const { fid: folderId, id: eventId } = useParams();
  const dispatch = useDispatch();

  const thumbnailUrl = addParamsURL(decryptUrl(img.thumbnailFileSignedPath), {
    size: "s",
  });

  // ✅ Single source of truth for image state
  const [imgSrc, setImgSrc] = useState(thumbnailUrl);

  const handleCardClick = (event) => {
    if (selectedId.length > 0) {
      onCheckBoxClick(!isActive, index, img.id, event);
    } else {
      onClick(index, event);
    }
  };

  const { sendRequest: thumbnailRequest } = useApiHttp();
  const { sendRequest: downloadRequest } = useApiHttp();

  // ✅ Prevent repeated error state updates
  const handleImageError = useCallback(() => {
    setImgSrc((prev) => (prev === brokenImg ? prev : brokenImg));
  }, []);

  // ✅ Sync if thumbnail URL changes
  useEffect(() => {
    setImgSrc(thumbnailUrl);
  }, [thumbnailUrl]);

  const downloadHande = (mediaId) => {
    downloadRequest(
      {
        url: `organizer/download`,
        method: "POST",
        base: "MEDIA",
        body: {
          mediaId,
          eventId,
        },
      },
      null,
      (data, res) => {
        const imageUrl = URL.createObjectURL(data);
        const fileName = getFileNameFromDisposition(res);
        downloadFile(imageUrl, fileName);
      },
      (err, data) => {
        Notifications.toast.error({
          id: "download-photos-error",
          title: data?.message,
        });
      },
    );
  };

  const thumbnailUpdate = (thumbnail) => {
    thumbnailRequest(
      {
        url: `event/update-thumbnail`,
        method: "PUT",
        body: {
          eventId,
          folderId: eventFID,
          thumbnail,
        },
      },
      null,
      (data) => {
        Notifications.toast.success({
          id: "upload-thumbnail",
          title: data?.message,
        });
      },
      (err, data) => {
        Notifications.toast.error({
          id: "upload-thumbnail",
          title: data?.message,
        });
      },
    );
  };

  return (
    <div
      className={`${classes["card-gallery"]}`}
      onClick={handleCardClick}
      onFocus={() => onTileFocus(index)}
      ref={(node) => registerPhotoTileRef(index, node)}
      role="option"
      aria-selected={isActive}
      tabIndex={isFocused ? 0 : -1}
      data-photo-index={index}
      key={index}
    >
      <div className={`${classes["image-card"]}`}>
        {/* Image */}
        <img
          src={imgSrc}
          alt={img.fileName}
          loading="lazy"
          onError={handleImageError}
        />

        {/* Hover Overlay */}
        <div
          className={`${classes["hover-overlay"]}  ${isActive || selectedId.length > 0 ? classes["hover-overlay-active"] : ""} ${selectedId.length > 0 ? classes["check-box-active"] : ""}`}
        >
          <div className={`${classes["hover-header"]}`}>
            <div />
            {selectedTab !== "approvedPhotosAsPhotographer" && (
              <div className={`${classes["header-options"]}`}>
                <Box
                  className={`${classes["view-button"]}`}
                  sx={{
                    "--color-primary-grey-60": "white",
                  }}
                  title="View"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick(index, e);
                  }}
                >
                  <Icon ic={"IcZoomIn"} color="grey-60" size="m" />
                </Box>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={isActive}
                    onChange={(e) =>
                      onCheckBoxClick(e.target.checked, index, img.id, e)
                    }
                    sx={{
                      color: "white",
                      "&.Mui-checked": {
                        color: "white",
                      },
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={`${classes["bottom-bar"]}`}>
          {/* <div className={`${classes["photo-details"]}`}>
            <span title={img.fileName}>{img.fileName}</span>
            <span title={img.userName}>~ {img.userName}</span>
          </div> */}

          <div className={`${classes["photo-actions"]}`}>
            {isAdmin && (
              <Box
                sx={{ "--color-primary-grey-60": "white" }}
                title="Download"
                onClick={(e) => {
                  e.stopPropagation();
                  downloadHande(img.id);
                }}
              >
                <Icon ic="IcDownload" color="grey-60" size="m" />
              </Box>
            )}

            {isAdmin && selectedTab !== "photographersUploadedPhotos" && (
              <Box
                sx={{ "--color-primary-grey-60": "white" }}
                title="Set Thumbnail"
                onClick={(e) => {
                  e.stopPropagation();
                  thumbnailUpdate(decryptUrl(img.reducedFileSignedPath));
                }}
              >
                <Icon ic="IcScanPicture" color="grey-60" size="m" />
              </Box>
            )}
            {isAdmin && (
              <Box
                sx={{
                  "--color-primary-grey-60": "white",
                }}
                title="Image info"
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(setFileDetails(img));
                  dispatch(openFileSideBar());
                }}
              >
                <Icon ic={"IcInfo"} color="grey-60" size="m" />
              </Box>
            )}
          </div>
        </div>
      </div>
      <div className={`${classes["card-bottom"]}`}>
        <div className={`${classes["photo-info"]}`}>
          <span
            title={img.fileName}
            className={`${classes["wrap-txt"]} ${classes["file-name"]}`}
          >
            {img.fileName}
          </span>
          <span title={bytesToHumanReadable(img?.fileSize)}>
            {bytesToHumanReadable(img?.fileSize)}
          </span>
        </div>

        <div className={`${classes["photo-info"]}`}>
          <span title={img.userName} className={`${classes["wrap-txt"]}`}>
            ~ {img.userName}
          </span>
          <span title={getArrtoDate(img?.createdAt)}>
            {getArrtoDate(img?.createdAt)}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ===============================
   MAIN GRID COMPONENT
   =============================== */
export default function CardGallery({
  images = [],
  endReached = () => {},
  onCheckBoxChange = () => {},
  onClick = () => {},
  loading = false,
  selectedId = [],
  isAdmin = false,
  selectedTab,
  focusedIndex = 0,
  onTileFocus = () => {},
  registerPhotoTileRef = () => {},
}) {
  const handleSetThumbnail = (img) => {
    console.log("Thumbnail set for:", img);
  };

  return (
    <div className={`${classes["grid-container"]}`}>
      <VirtuosoGrid
        totalCount={images.length}
        endReached={endReached}
        className="custom-scrollbar"
        overscan={200}
        components={{ ...gridComponents, Footer: loading ? Footer : null }}
        itemContent={(index, item) => (
          <ImageItem
            key={index}
            img={images[index]}
            index={index}
            onCheckBoxClick={onCheckBoxChange}
            onSetThumbnail={handleSetThumbnail}
            isActive={selectedId.includes(images[index].id)}
            isAdmin={isAdmin}
            selectedTab={selectedTab}
            isPublic={images[index].isPublic}
            onClick={onClick}
            selectedId={selectedId}
            isFocused={focusedIndex === index}
            onTileFocus={onTileFocus}
            registerPhotoTileRef={registerPhotoTileRef}
          />
        )}
      />
    </div>
  );
}

/* ===============================
   FOOTER LOADER
   =============================== */
const Footer = () => (
  <Loader size="small" containerProps={{ style: { marginTop: 16 } }} />
);
